import { Link } from 'react-router-dom';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { EVENT_KIND_LABEL } from '@/features/publicsite/events/eventKind';
import { itemTextLang } from '@/features/publicsite/contentLanguage';
import { eventTextLang } from '@/features/publicsite/events/eventLanguage';
import { PUBLIC_EVENT_STATUS } from '@/features/publicsite/events/eventStatus';
import { splitTimeline } from '@/features/publicsite/events/eventTimeline';
import type { EventItem } from '@/features/publicsite/types/events/EventItem';
import type { CalendarRowProps } from '@/features/publicsite/types/home/CalendarRowProps';
import type { TrainingSession } from '@/features/publicsite/types/TrainingSession';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { eventPath } from '@/features/publicsite/eventPath';
import { calendarLocale } from '@/features/publicsite/monthCalendar';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicHome } from '@/i18n/dictionaries/publicHome';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * One row of either calendar — the training sessions and the events lists render the same
 * markup, and used to render it twice. That duplication is why the row shipped broken: the
 * separator between its parts had been put back on the training side and left off the events
 * side, so one list read "Instruire Irigare prin picurare" and the other "DEMONSTRATIONAtelier".
 *
 * The parts arrive as plain strings and this decides everything about how they are joined, once.
 */
function CalendarRow({
  to,
  day,
  month,
  category,
  title,
  meta,
  titleLang,
  metaLang,
}: CalendarRowProps) {
  // Thymeleaf had these spans on separate source lines and HTML collapsed every newline into a
  // space. JSX emits no text node between siblings, so without this the row is one run: a screen
  // reader announces it as a single word and copying it out of the page yields it joined. The
  // space goes inside the string rather than between the elements because whitespace written
  // between JSX siblings is dropped, and Prettier reinstates it as {' '} on the next format.
  // A margin would satisfy the eye and neither the screen reader nor the clipboard.
  const parts = [category, title, ...meta].filter((p): p is string => !!p && p.trim() !== '');
  const [cat, name, ...rest] = parts.map((part, i, all) =>
    i < all.length - 1 ? `${part} ` : part,
  );

  return (
    <Link className="cal-item" to={to}>
      <span className="cal-item__date">
        <strong>{day}</strong>
        <span>{month}</span>
      </span>
      {/* cal-item__main, the same wrapper the /evenimente rows use: `flex: 1` so the text takes
        the width the date box leaves, `min-width: 0` so a long title wraps inside it rather than
        pushing the row wider. Unclassed, this span was a bare inline box and did neither. */}
      <span className="cal-item__main">
        <span className="cal-item__cat">{cat}</span>
        <span className="cal-item__title" lang={titleLang}>
          {name}
        </span>
        <span className="cal-item__meta">
          {rest.map((part, i) => (
            <span key={`${i}-${part}`} lang={metaLang}>
              {part}
            </span>
          ))}
        </span>
      </span>
    </Link>
  );
}

/**
 * The two calendars: training sessions on the left, events on the right.
 *
 * One component, because they render one row — see {@link CalendarRow} — and the defect that
 * produced that shared row was the two lists drifting apart while they were two copies.
 *
 * Event kinds are read from publicEvents, the dictionary /evenimente already uses. That lookup was
 * dropped as an unused import during the monorepo split, which left this calendar printing the
 * enum: one event read "Demonstrație practică" on /evenimente and "DEMONSTRATION" here.
 */
export function HomeCalendars() {
  const { t } = useTranslation(publicHome);
  const { language } = useLanguage();
  const loc = calendarLocale(language);

  const allTrainings = usePublicList<TrainingSession>(publicCatalogApi.trainingsUrl);
  const allEvents = usePublicList<EventItem>(publicCatalogApi.eventsUrl);
  const trainings = allTrainings?.slice(0, 3) ?? null;
  // "Evenimente apropiate": the calendar answers past and cancelled events too, and slicing it as it
  // came put events that had already happened, or would not, under that heading.
  const events = allEvents
    ? splitTimeline(allEvents)
        .upcoming.filter((e) => e.status !== PUBLIC_EVENT_STATUS.cancelled)
        .slice(0, 3)
    : null;

  return (
    <section className="section">
      <div className="container cal-grid">
        <div>
          <div className="section-head section-head--stacked">
            <span className="eyebrow">{t('trainingsEyebrow')}</span>
            <h2 className="cal-grid__heading">{t('trainingsHeading')}</h2>
          </div>
          <div className="cal-card">
            <div className="cal-card__head">
              <h3>{t('trainingsNext')}</h3>
              <Link to="/instruiri">{t('trainingsAll')}</Link>
            </div>
            {trainings?.map((s) => {
              const d = new Date(s.startsAt);
              return (
                <CalendarRow
                  key={s.sessionId}
                  to="/instruiri"
                  day={String(d.getDate()).padStart(2, '0')}
                  month={String(d.getMonth() + 1).padStart(2, '0')}
                  category={s.online ? t('trainingsOnline') : t('trainingsOnSite')}
                  title={s.programTitle}
                  meta={[s.institution, s.location]}
                  // The programme's title follows its own version (T234); the institution's name
                  // is registered in one language and the line holds both, so it carries none.
                  titleLang={itemTextLang(s, 'programTitle', language)}
                />
              );
            })}
            {trainings && trainings.length === 0 && (
              <p className="empty-state">{t('emptyState')}</p>
            )}
            <div className="cal-card__foot">
              <Link to="/instruiri">{t('trainingsSeeAll')}</Link>
            </div>
          </div>
        </div>
        <div>
          <div className="section-head section-head--stacked">
            <span className="eyebrow">{t('eventsEyebrow')}</span>
            <h2 className="cal-grid__heading">{t('eventsHeading')}</h2>
          </div>
          <div className="cal-card">
            <div className="cal-card__head">
              <h3>{t('eventsNext')}</h3>
              <Link to="/evenimente">{t('eventsAll')}</Link>
            </div>
            {events?.map((e) => {
              const d = new Date(e.startsAt);
              return (
                <CalendarRow
                  key={e.id}
                  to={eventPath(e)}
                  day={String(d.getDate()).padStart(2, '0')}
                  month={d.toLocaleDateString(loc, { month: 'short' })}
                  category={t(EVENT_KIND_LABEL[e.kind])}
                  title={e.title}
                  meta={[e.location]}
                  titleLang={eventTextLang(e, 'title', language)}
                  metaLang={eventTextLang(e, 'location', language)}
                />
              );
            })}
            {events && events.length === 0 && <p className="empty-state">{t('emptyState')}</p>}
            <div className="cal-card__foot">
              <Link to="/evenimente">{t('eventsSeeAll')}</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
